import React, { ReactNode } from 'react';
import { IonModal, IonText } from '@ionic/react';
import { useInstance } from '../support/ioc/useInstance';
import { useSubscription } from '@autopylot-internal/state';
import { RevenueCatModel } from './revenue-cat/RevenueCatModel.ts';
import { RevenueCatSDKPurchaseError } from './revenue-cat/PurchasesErrorAdapter.ts';
import { AlertPresenter } from '../app/AlertBinder.tsx';
import { PaywallDialogViewModel } from './PaywallDialogViewModel.tsx';
import { OperatorState } from '../profile/OperatorState.ts';
import { Spinner } from '../components/spinner/Spinner.tsx';
import { StandardDialogContent } from './StandardDialogContent.tsx';
import { PromotionDialogContent } from './PromotionDialogContent.tsx';
import { NewUserDialogContent } from './NewUserDialogContent.tsx';
import { FloatingCloseButton } from '../components/button/FloatingCloseButton.tsx';
import { AutoPylotPlus } from './revenue-cat/AutoPylotPlus.tsx';

export const PaywallDialog: React.FC = () => {
    const alert = useInstance(AlertPresenter);

    const revenueCat = useInstance(RevenueCatModel);
    useSubscription(() => revenueCat);

    const operator = useInstance(OperatorState);
    useSubscription(() => operator);

    const dialogVM = useInstance(PaywallDialogViewModel);
    useSubscription(() => dialogVM);

    const trialDays = revenueCat.getTrialDays();
    const term = dialogVM.state.paymentTerm;

    const hasUpgraded = revenueCat.hasUpgraded();
    const isEligible = revenueCat.isEligibleForTrial();

    const showNewUserContent = !hasUpgraded && isEligible && dialogVM.isNewUser();
    const showPromotion = !hasUpgraded && isEligible && dialogVM.isPromotionActive();

    const upgrade = async () => {
        try {
            dialogVM.setState({ isWorking: true });

            // guest must sign up before continuing
            let message: ReactNode;
            if (!isEligible || (showPromotion && term === 'annual')) {
                // annual promotion doesn't have free trial
                message = <>Sign up now to get all the benefits of <IonText color="primary">AutoPylot+</IonText></>;
            } else {
                message = <>Sign up now and redeem your<br/>free {trialDays}-day trial of <IonText color="primary">AutoPylot+</IonText></>;
            }
            const imageUrl = '/assets/images/auth/paywall-auth-incentive-v1.png';
            if (!operator.ensureLoggedIn({ message, imageUrl, from: 'Paywall', returnTo: dialogVM.getReturnToUrl({ term }) })) {
                return;
            }

            await revenueCat.upgrade(term);
            dialogVM.close();
        } catch (e: any) {
            if (e && e.errorType === 'RevenueCatSDKPurchaseError' && (e as RevenueCatSDKPurchaseError).cancelled){
                // then do nothing
            } else {
                alert.showAndLogError(e);
            }
        } finally {
            dialogVM.setState({ isWorking: false });
        }
    };

    const restore = async () => {
        try {
            dialogVM.setState({ isWorking: true });
            await revenueCat.restore();
            dialogVM.close();
        } catch (e: any) {
            if (e && e.errorType === 'RevenueCatSDKPurchaseError' && (e as RevenueCatSDKPurchaseError).cancelled){
                // then do nothing
            } else {
                alert.showAndLogError(e);
            }
        } finally {
            dialogVM.setState({ isWorking: false });
        }
    };

    return (
        <IonModal
            data-id="autopylot-plus"
            isOpen={dialogVM.state.isOpen}
            onWillDismiss={dialogVM.close}
            keepContentsMounted={true}
        >
            <FloatingCloseButton onClick={dialogVM.close} />

            {/* check isOpen so these remount each time and fire their PaywallOpened events */}
            {dialogVM.state.isOpen &&
                <>
                    {revenueCat.isDown() ? (
                        <div style={{padding: '50px 100px', textAlign: 'center'}}>
                            <h1><AutoPylotPlus /></h1>
                            <p>
                                We're sorry, we are currently experiencing difficulties with our paywall.
                                Please bear with us as we work to get it back up and running.
                            </p>
                            <p>
                                (In the meantime, AutoPylot+ features are temporarily available to everyone.)
                            </p>
                        </div>
                    ) : showPromotion ? (
                        <PromotionDialogContent onUpgrade={upgrade} onRestore={restore} />
                    ) : showNewUserContent ? (
                        <NewUserDialogContent onUpgrade={upgrade} onRestore={restore} />
                    ) : (
                        <StandardDialogContent onUpgrade={upgrade} onRestore={restore} />
                    )}

                    {dialogVM.state.isWorking &&
                        <Spinner centered />
                    }
                </>
            }
        </IonModal>
    );
};
